import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listPlotReadingStatuses,
  saveDraftReading,
  updateDraftReading,
  attachReadingPhoto,
  previewMeterReadingImport,
  confirmMeterReadingImport,
  type SaveReadingInput,
  type UpdateReadingInput,
  type MeterReadingImportRequest,
} from '@/lib/meter-readings'

function key(utilityTypeId: string, billingPeriod: string) {
  return ['meter-readings', utilityTypeId, billingPeriod] as const
}

export function usePlotReadingStatuses(utilityTypeId: string, billingPeriod: string) {
  return useQuery({
    queryKey: key(utilityTypeId, billingPeriod),
    queryFn: () => listPlotReadingStatuses(utilityTypeId, billingPeriod),
    enabled: Boolean(utilityTypeId && billingPeriod),
  })
}

export function useSaveDraftReading(utilityTypeId: string, billingPeriod: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SaveReadingInput) => saveDraftReading(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(utilityTypeId, billingPeriod) }),
  })
}

export function useUpdateDraftReading(utilityTypeId: string, billingPeriod: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateReadingInput }) =>
      updateDraftReading(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(utilityTypeId, billingPeriod) }),
  })
}

export function useAttachReadingPhoto(utilityTypeId: string, billingPeriod: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, photo }: { id: string; photo: File }) => attachReadingPhoto(id, photo),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(utilityTypeId, billingPeriod) }),
  })
}

export function usePreviewMeterReadingImport() {
  return useMutation({
    mutationFn: (input: MeterReadingImportRequest) => previewMeterReadingImport(input),
  })
}

export function useConfirmMeterReadingImport(utilityTypeId: string, billingPeriod: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: MeterReadingImportRequest) => confirmMeterReadingImport(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(utilityTypeId, billingPeriod) }),
  })
}
